import type { FC } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { cn } from '@opendoor/atoms/utils';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';

interface FloatingCTAProps {
  ctaText: string;
  onClickCta: () => void;
  ctaHref?: string;
  ctaAnalyticsName?: string;
  isFavoriteLoading?: boolean;
  isLiked?: boolean;
  onToggleFavorite?: () => void;
  variant?: 'secondary' | 'primary' | 'secondaryInverse';
  isVisible?: boolean;
  disabled?: boolean;
  // Secondary button props
  showSecondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  onClickSecondary?: () => void;
  secondaryAnalyticsName?: string;
}

export const FloatingCTA: FC<FloatingCTAProps> = ({
  ctaText,
  onClickCta,
  ctaHref,
  ctaAnalyticsName = 'cosmos-marketplace-footer-cta',
  isFavoriteLoading,
  isLiked,
  onToggleFavorite,
  variant = 'primary',
  isVisible = true,
  disabled = false,
  showSecondaryButton = false,
  secondaryButtonText,
  secondaryButtonHref,
  onClickSecondary,
  secondaryAnalyticsName = 'cosmos-marketplace-footer-secondary-cta',
}) => {
  return (
    <Col
      className={cn(
        'sticky bottom-0 left-0 right-0 z-[4] w-full px-3 pb-3 transition-all duration-300 ease-in-out max-md:fixed md:px-6 md:pb-6',
        isVisible
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-5 opacity-0',
      )}
    >
      <Row className="w-full items-center justify-between rounded-3xl border border-od-border-hover-tertiary bg-od-bg-primary py-3 pl-4 pr-3 shadow-lg">
        <Button
          variant="secondary"
          aria-label={isLiked ? 'unfavorite' : 'favorite'}
          analyticsName="cosmos-marketplace-pdp-favorite"
          onClick={onToggleFavorite}
          loading={isFavoriteLoading}
          className="size-7 rounded-full border-od-border-tertiary p-0"
        >
          {isLiked ? (
            <IconHeartFilled size={20} className="text-od-content-error" />
          ) : (
            <IconHeart size={20} />
          )}
        </Button>
        <Row className="items-center gap-2">
          {showSecondaryButton && secondaryButtonText && (
            <Button
              analyticsName={secondaryAnalyticsName}
              aria-label={secondaryButtonText}
              variant="secondary"
              size="lg"
              onClick={onClickSecondary}
              href={secondaryButtonHref}
              target="_blank"
              rel="noopener"
            >
              {secondaryButtonText}
            </Button>
          )}
          {/* Guard href with !disabled so a disabled CTA renders as a real
              disabled <button> instead of an inert anchor. */}
          <Button
            analyticsName={ctaAnalyticsName}
            aria-label={ctaText}
            variant={variant}
            size="lg"
            onClick={onClickCta}
            disabled={disabled}
            href={disabled ? undefined : ctaHref}
            target="_blank"
            rel="noopener"
          >
            {ctaText}
          </Button>
        </Row>
      </Row>
    </Col>
  );
};
